// src/monitoring/MonitoringContext.jsx
//
// One load of the live portfolio shared by every Monitoring page, plus the
// project / auth scope. Pages that need more (a park's detail, the alarm
// list) fetch it themselves and call refresh() when they change something.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFile } from "../context/FileContext";
import { useAuth } from "../context/AuthContext";
import { fetchPortfolio } from "./services/monitoringApi";
import { useDemo } from "../context/DemoContext";

const MonitoringContext = createContext(null);

export function MonitoringProvider({ children }) {
  const { currentProject } = useFile();
  const { accessToken } = useAuth();
  const { demoProfile } = useDemo();
  const projectId = currentProject?.id;
  const [state, setState] = useState({ projectId: null, data: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    if (!projectId || !accessToken) {
      setState({ projectId: null, data: null });
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    fetchPortfolio(projectId, { accessToken, signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setState({ projectId, data }); })
      .catch((err) => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, accessToken, revision]);

  // Live screens refresh themselves every two minutes while mounted.
  useEffect(() => {
    if (!projectId || !accessToken) return undefined;
    const id = setInterval(() => setRevision((v) => v + 1), 120000);
    return () => clearInterval(id);
  }, [projectId, accessToken]);

  const refresh = useCallback(() => setRevision((v) => v + 1), []);
  const portfolio = state.projectId === projectId ? state.data : null;

  const value = useMemo(() => ({
    projectId, projectName: currentProject?.name, accessToken, authenticated: Boolean(accessToken),
    canSeedDemo: Boolean(demoProfile?.regularIsAdmin),
    portfolio, parks: portfolio?.parks || [], loading, error, loaded: Boolean(portfolio), refresh,
  }), [projectId, currentProject?.name, accessToken, demoProfile?.regularIsAdmin, portfolio, loading, error, refresh]);

  return <MonitoringContext.Provider value={value}>{children}</MonitoringContext.Provider>;
}

export function useMonitoring() {
  const value = useContext(MonitoringContext);
  if (!value) throw new Error("Monitoring context is unavailable.");
  return value;
}
